import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import { UUID } from "@goose/utils/uuid";

import { Breadcrumbs } from "~/components/admin/Breadcrumbs";
import { DetailsTable } from "~/components/admin/DetailsTable";
import { UsersIcon } from "~/components/admin/Icons";
import { PageHeader } from "~/components/admin/PageHeader";
import { UserEditDialogButton } from "~/components/admin/UserEditDialogButton";
import { UserSalesforceUsersTable } from "~/components/admin/UserSalesforceUsersTable";
import { ErrorDisplay } from "~/components/ErrorDisplay";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Badge } from "~/components/ui/Badge";
import { Button } from "~/components/ui/Button";
import { Spinner } from "~/components/ui/Spinner";
import { formatDate } from "~/lib/format";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute("/_authenticated/admin/users/$userId")({
  component: UserPage,
  params: {
    parse: ({ userId }) => {
      return { userId: UUID.from(userId) };
    },
    stringify: ({ userId }) => {
      return { userId: userId.toShort() };
    },
  },
  beforeLoad: ({ params, location }) => {
    // Canonicalize long-form UUID URLs to the Base58 short form.
    if (!location.pathname.endsWith(params.userId.toShort())) {
      throw redirect({
        to: "/admin/users/$userId",
        params,
        replace: true,
      });
    }
  },
});

function getLogUrl(userId: string) {
  const url = "https://console.cloud.google.com/logs/query";
  const project = encodeURIComponent(process.env.GOOGLE_CLOUD_PROJECT);
  const query = encodeURIComponent(
    `jsonPayload.properties.user.id="${userId}"`,
  );
  return `${url};query=${query}?project=${project}`;
}

function SalesforceUsersSection({ userId }: { userId: UUID }) {
  const trpc = useTRPC();
  const query = useQuery(
    trpc.salesforce.users.listByUser.queryOptions({
      userId: userId.toString(),
    }),
  );

  return (
    <QueryState
      result={query}
      loading={<Spinner size="lg" />}
      error={(error) => <TRPCClientErrorDisplay error={error} />}
      empty={
        <ErrorDisplay message="No Salesforce users found" level="warning" />
      }
    >
      {(data) => (
        <UserSalesforceUsersTable userId={userId} salesforceUsers={data} />
      )}
    </QueryState>
  );
}

function UserPage() {
  const trpc = useTRPC();
  const { userId } = Route.useParams();

  const query = useQuery(
    trpc.users.get.queryOptions({ id: userId.toString() }),
  );

  return (
    <div className="space-y-6">
      <QueryState
        result={query}
        loading={<Spinner size="lg" />}
        error={(error) => <TRPCClientErrorDisplay error={error} />}
        empty={<ErrorDisplay message="User not found" level="warning" />}
      >
        {(user) => (
          <>
            <PageHeader
              breadcrumbs={
                <Breadcrumbs>
                  <Link
                    to="/admin"
                    className="transition-colors hover:text-slate-700"
                  >
                    Admin
                  </Link>
                  <Link
                    to="/admin/users"
                    className="transition-colors hover:text-slate-700"
                  >
                    Users
                  </Link>
                  <span className="text-slate-700">
                    {user.name ?? user.email}
                  </span>
                </Breadcrumbs>
              }
              icon={UsersIcon}
              title={user.name ?? user.email}
              actions={
                <>
                  <Button
                    as="a"
                    size="sm"
                    href={getLogUrl(user.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outline"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    View logs
                  </Button>
                  <UserEditDialogButton user={user} size="sm" />
                </>
              }
            />

            <DetailsTable
              details={[
                {
                  label: "ID",
                  value: user.id,
                },
                {
                  label: "Name",
                  value: user.name ?? "—",
                },
                {
                  label: "Email",
                  value: user.email,
                },
                {
                  label: "Roles",
                  value: (
                    <div className="flex flex-wrap gap-2">
                      {user.roles.map((role) => (
                        <Badge key={role} variant="outline" size="sm">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  ),
                },
                {
                  label: "Created",
                  value: formatDate(user.createdAt),
                },
                {
                  label: "Updated",
                  value: formatDate(user.updatedAt),
                },
              ]}
            />

            <SalesforceUsersSection userId={userId} />
          </>
        )}
      </QueryState>
    </div>
  );
}
